import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css';
import '@splidejs/splide/css/core';
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import { Grid } from "@splidejs/splide-extension-grid";
import Markdown from "react-markdown";
import ReactPlayer from "react-player/lazy";
import { useNavigate } from "react-router-dom";
import Button from "../Button";
import { BlockType } from "../../common/constants";
import { Each } from "../../common/Each";
import useWindowDimensions from "../../common/hooks/useWindowDimensions";
import CollapsableBlock from "../builder/CollapsableBlock";
import EditionsCarousel from "../builder/EditionsCarousel";
import styles from "./PageViewer.module.css";
import SvgRenderer from "../SvgRenderer";
import FAQCollapsible from "../FAQCollapsible";
import rehypeRaw from 'rehype-raw'
import MainContext from "../../common/MainContext";
import { useCallback, useContext, useEffect, useRef } from "react";
import ContactFormBlock from '../ContactFormBlock';

const ACTION_EVENT_MAP = {
    onHover: { apply: 'mouseenter', reset: 'mouseleave' },
    onClick: { apply: 'click' },
};

const PageViewer = ({ page, data }) => {

    return (
        <Each of={page} render={(section) => {
            return <PageBlockViewer
                key={section.id}
                block={section}
                data={data}
            />
        }} />
    )

}

const PageBlockViewer = ({ block, data }) => {

    useContext(MainContext)

    const navigate = useNavigate()
    const { width } = useWindowDimensions()
    const actionStateCache = useRef(new Map())

    const blockStyles = {
        section: styles.section,
        column: styles.column,
        row: styles.row,
        text: styles.text,
        markdown: styles.markdown,
        image: styles.image,
        video: styles.video,
        button: styles.button,
        collapsable: styles.collapsable,
        carousel: styles.carousel,
        form: styles.form,
    };

    const moveToElement = (id) => {
        const elem = document.getElementById(id.replace('#', ''));
        if (elem) {
            let position = elem.getBoundingClientRect();
            window.scrollTo({ top: position.top - 80, behavior: 'smooth' });

        }
    }

    const getViewStyle = () => {
        if (width > 768)
            return block.style
        else if (width > 540 && width <= 768)
            return { ...block.style, ...block.styleTablet }
        else
            return { ...block.style, ...block.styleTablet, ...block.styleMobile }
    }

    const getGridRows = useCallback(() => {
        if (width > 768)
            return block.data?.grid?.rows ?? 1
        else if (width > 540 && width <= 768)
            return block.data?.breakpoints?.[768]?.grid?.rows ?? 2
        else
            return block.data?.breakpoints?.[540]?.grid?.rows ?? 1
    }, [width, block.data])

    const getGridColumns = useCallback(() => {
        if (width > 768)
            return block.data?.grid?.cols ?? 1
        else if (width > 540 && width <= 768)
            return block.data?.breakpoints?.[768]?.grid?.cols ?? 2
        else
            return block.data?.breakpoints?.[540]?.grid?.cols ?? 1
    }, [width, block.data])

    const getCarouselViewPerPage = useCallback(() => {
        if (width > 768)
            return block.data?.perPage ?? 1
        else if (width > 540 && width <= 768)
            return block.data?.breakpoints?.[768]?.perPage ?? 2
        else
            return block.data?.breakpoints?.[540]?.perPage ?? 1
    }, [width, block.data])

    const getCarouselViewPerMove = useCallback(() => {
        if (width > 768)
            return block.data?.perMove ?? 1
        else if (width > 540 && width <= 768)
            return block.data?.breakpoints?.[768]?.perMove ?? block.data?.perMove ?? 1
        else
            return block.data?.breakpoints?.[540]?.perMove ?? block.data?.perMove ?? 1
    }, [width, block.data])

    const checkButtonDisabled = () => {
        return !block.data?.url || block.data?.url.trim() === ''
    }

    useEffect(() => {
        if (!block.data?.actions?.length) {
            return;
        }

        const element = document.getElementById(block.id);
        if (!element) {
            return;
        }

        const detachListeners = [];

        block.data.actions.forEach(action => {
            const eventConfig = ACTION_EVENT_MAP[action.type];
            const hasProperties = action.properties && Object.keys(action.properties).length > 0;
            const hasAttributes = action.attributes && Object.keys(action.attributes).length > 0;

            if (!eventConfig || (!hasProperties && !hasAttributes)) {
                return;
            }

            let target = document.getElementById(action.target.replace('#', ''));
            if (!target) {
                return;
            }
            if (target.className.includes('video')) {
                target = target.getElementsByTagName('video')[0];
            }

            const { apply: applyEvent, reset: resetEvent } = eventConfig;

            const actionKey = `${block.id}-${action.target}-${action.type}`;

            const saveOriginalState = () => {
                if (actionStateCache.current.has(actionKey)) {
                    return;
                }

                const originals = {
                    styles: {},
                    attributes: {},
                    transition: target.style.transition,
                };

                if (hasProperties) {
                    Object.keys(action.properties).forEach(property => {
                        originals.styles[property] = target.style[property];
                    });
                }

                if (hasAttributes) {
                    Object.keys(action.attributes).forEach(attribute => {
                        originals.attributes[attribute] = target.getAttribute(attribute);
                    });
                }

                actionStateCache.current.set(actionKey, originals);
            };

            const restoreOriginalState = () => {
                const originals = actionStateCache.current.get(actionKey);
                if (!originals) {
                    return;
                }

                if (hasProperties) {
                    Object.entries(originals.styles).forEach(([property, value]) => {
                        if (value === undefined || value === null || value === '') {
                            target.style.removeProperty(property);
                        } else {
                            target.style[property] = value;
                        }
                    });
                }

                if (hasAttributes) {
                    Object.entries(originals.attributes).forEach(([attribute, value]) => {
                        if (value === null || value === undefined) {
                            target.removeAttribute(attribute);
                        } else {
                            target.setAttribute(attribute, value);
                        }
                    });
                }

                target.style.transition = originals.transition ?? '';

                actionStateCache.current.delete(actionKey);
            };

            const applyEffects = () => {
                saveOriginalState();

                if (hasProperties) {
                    target.style.transition = action.transition ?? 'all 0.1s ease-in-out';
                    Object.entries(action.properties).forEach(([property, value]) => {
                        target.style[property] = value;
                    });
                }

                if (hasAttributes) {
                    Object.entries(action.attributes).forEach(([attribute, value]) => {
                        if (value === null || value === undefined) {
                            target.removeAttribute(attribute);
                        } else {
                            target.setAttribute(attribute, value);
                        }
                    });
                }
            };

            const resetEffects = () => {
                restoreOriginalState();
            };

            element.addEventListener(applyEvent, applyEffects);
            if (resetEvent) {
                element.addEventListener(resetEvent, resetEffects);
            }

            detachListeners.push(() => {
                element.removeEventListener(applyEvent, applyEffects);
                if (resetEvent) {
                    element.removeEventListener(resetEvent, resetEffects);
                }
                restoreOriginalState();
            });

        });

        return () => {
            detachListeners.forEach(detach => detach());
        };

    }, [block]);

    useEffect(() => {
        if (block.type !== BlockType.Video) {
            return;
        }

        const container = document.getElementById(block.id);
        if (!container) {
            return;
        }

        const videoEl = container.querySelector('video');
        if (!videoEl) {
            return;
        }

        const syncPlayback = () => {
            const shouldPlay = videoEl.getAttribute('data-playing') === 'true';
            if (shouldPlay) {
                const playPromise = videoEl.play();
                if (playPromise?.catch) {
                    playPromise.catch(() => { });
                }
            } else {
                videoEl.pause();
            }
        };

        syncPlayback();

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-playing') {
                    syncPlayback();
                }
            }
        });

        observer.observe(videoEl, { attributes: true, attributeFilter: ['data-playing'] });

        return () => {
            observer.disconnect();
        };
    }, [block]);

    const getBlockStyle = () => {
        let result = getViewStyle()
        if ([BlockType.Button, BlockType.Video, BlockType.Icon].includes(block.type)) {
            return { width: result?.width ?? 'fit-content', height: result?.height ?? 'fit-content' }
        }
        if (block.type === BlockType.Collapsable) {
            return { width: result?.width ?? '100%' }
        }
        return result
    }

    const getSplideExtensions = () => {
        if (block && block.data){
            const extensions = {};
            if (block.data.autoScroll) {
                extensions['AutoScroll'] = AutoScroll;
            }
            if (block.data.grid) {
                extensions['Grid'] = Grid;
            }
            return extensions;
        }
    }

    const normalizeAutoScrollSpeed = (rawSpeed) => {
        const parsed = Number(rawSpeed);
        if (!Number.isFinite(parsed)) {
            return 1;
        }

        let speed = parsed;
        const abs = Math.abs(speed);
        if (abs > 100) {
            speed = speed / 1000;
        } else if (abs > 10) {
            speed = speed / 10;
        }

        return Math.max(-3, Math.min(3, speed));
    }

    return (
        <>
            <div
                id={block.id}
                className={`${blockStyles[block.type]}`}
                style={getBlockStyle()}
            >

                {block.type === BlockType.Text &&
                    <>
                        {block.data ?? ''}
                    </>
                }
                {
                    block.type === BlockType.Markdown &&
                    <Markdown rehypePlugins={[rehypeRaw]}>{block.data ?? ''}</Markdown>
                }
                {block.type === BlockType.Image &&
                    <>
                        {block.data && block.data?.url !== '' &&
                            <img
                                src={block.data.url ? block.data.url.replace('fra1.digitaloceanspaces', 'fra1.cdn.digitaloceanspaces') : block.data.replace('fra1.digitaloceanspaces', 'fra1.cdn.digitaloceanspaces')}
                                fetchpriority={block.data.fetchPriority ?? 'auto'}
                                loading={block.data.loading ?? 'eager'}
                                alt=''
                                className={styles.previewImage}
                                draggable="false"
                                style={getViewStyle()} />
                        }
                    </>
                }
                {block.type === BlockType.Video &&
                    <>
                        {block.data && block.data !== '' &&
                            <video src={block.data.url} style={getViewStyle()}
                                controls={block.data.controls ?? true}
                                autoPlay={block.data.autoplay ?? false}
                                loop={block.data.loop ?? false}
                                muted={block.data.muted ?? false}
                                poster={block.data.thumbnail}
                                playsInline={block.data.playsInline ?? false}
                                data-playing={(block.data.play ?? false).toString()}
                            />
                            //
                            // <ReactPlayer
                            //     url={block.data.url}
                            //     light={block.data.thumbnail}
                            //     playing={block.data.autoplay ?? false}
                            //     loop={block.data.loop ?? false}
                            //     muted={block.data.muted ?? false}
                            //     controls={block.data.controls ?? true}
                            //     width="100%"
                            //     height="auto"
                            //     className={styles.player}
                            //     style={{ ...getViewStyle(), }}
                            // />
                        }
                    </>
                }
                {
                    block.type === BlockType.Button &&
                    <Button
                        style={getViewStyle()}
                        fullWidth={block.data?.fullWidth}
                        accentColor={block.data?.accentColor}
                        textColor={block.data?.textColor}
                        inverse={block.data?.inverse}
                        appearance={block.data?.appearance}
                        disabled={checkButtonDisabled()}
                        ariaLabel={block.data?.ariaLabel}
                        onClick={() => {
                            let url = block.data?.url ?? '';

                            if (url?.startsWith('#')) {
                                moveToElement(url)
                            }
                            else if (url?.startsWith('http')) {
                                window.location.href = url
                            }
                            else if (url?.startsWith('mailto')) {
                                window.location.href = url
                            }
                            else if (url?.startsWith('tel')) {
                                window.location.href = url
                            }
                            else {
                                if (!isNaN(parseInt(url))) {
                                    navigate(parseInt(url))
                                }
                                else {
                                    navigate(url)
                                }
                            }
                        }}>
                        {block.data?.icon?.data && block.data?.icon?.position !== 'right' &&
                            <SvgRenderer svgString={block.data?.icon?.data} style={block.data?.icon?.style} />
                        }
                        {
                            block.data?.text || (block.data?.on_click === 'brochure' ? 'SCARICA BROCHURE' : "Bottone")
                        }
                        {block.data?.icon?.data && block.data?.icon?.position === 'right' &&
                            <SvgRenderer svgString={block.data?.icon?.data} style={block.data?.icon?.style} />
                        }
                    </Button>
                }


                {block.type === BlockType.Editions &&
                    <EditionsCarousel course_id={block.data?.course_id} accentColor={block.data?.accentColor} />
                }

                {block.type === BlockType.Icon &&
                    <>
                        {block.data?.icon?.data &&
                            <SvgRenderer svgString={block.data?.icon?.data} style={getViewStyle()} />
                        }
                    </>
                }

                {block.type === BlockType.HTML &&
                    <div dangerouslySetInnerHTML={{ __html: block.data?.html }} />
                }

                {block.type === BlockType.Faq &&
                    <FAQCollapsible style={getViewStyle()} title={block.data?.title} description={block.data?.description} />
                }

                {block.type === BlockType.Collapsable &&
                    <CollapsableBlock
                        title={block.data?.title}
                        style={getViewStyle()}
                    >
                        <Each of={block.content ?? []} render={(childBlock) => {
                            return <PageBlockViewer
                                block={childBlock}
                            />

                        }} />
                    </CollapsableBlock>
                }

                {block.type === BlockType.Form &&
                    <ContactFormBlock
                        data={block.data}
                    />
                }

                {block.type === BlockType.Carousel &&
                    <Splide
                        id={block.id}
                        extensions={getSplideExtensions()}
                        options={{
                            type: block.data?.type ?? 'slide',
                            drag: block.data?.drag,
                            autoplay: block.data?.autoplay,
                            grid: block.data?.grid ?? null,
                            speed: block.data?.speed ?? 300,
                            perPage: block.data?.perPage ?? 1,
                            perMove: block.data?.perMove ?? 1,
                            breakpoints: block.data?.breakpoints ?? {},
                            rewind: block.data?.rewind,
                            width: block.data?.width,
                            height: block.data?.height,
                            autoWidth: block.data?.autoWidth,
                            autoHeight: block.data?.autoHeight,
                            gap: block.data?.gap,
                            arrows: block.data?.arrows ?? true,
                            pagination: block.data?.pagination ?? true,
                            direction: block.data?.direction,
                            autoScroll: {
                                speed: normalizeAutoScrollSpeed(block.data?.autoScroll?.speed ?? 1),
                                rewind: block.data?.autoScroll?.rewind ?? false,
                                pauseOnHover: block.data?.autoScroll?.pauseOnHover ?? false,
                                pauseOnFocus: block.data?.autoScroll?.pauseOnFocus ?? false,
                            }
                        }}>
                        <Each of={block.content ?? []} render={(childBlock) => {
                            return <SplideSlide>
                                <PageBlockViewer
                                    block={childBlock}
                                />
                            </SplideSlide>
                        }} />
                    </Splide>
                }


                {block.content && block.content.length > 0 && ![BlockType.Carousel, BlockType.Collapsable].includes(block.type) &&
                    <Each of={block.content} render={(childBlock) => {
                        return <PageBlockViewer
                            key={childBlock.id}
                            block={childBlock}
                            data={data}
                        />
                    }} />
                }
            </div>
        </>

    )

}

export default PageViewer